import React, { useEffect, useMemo, useState } from 'react';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator/LoadingIndicator';
import { Banner } from '@havenengineering/module-shared-owners-ui/dist/components/Banner/Banner';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button/Button';
import { NumberedStepper } from '@havenengineering/module-shared-owners-ui/dist/components/NumberedStepper/NumberedStepper';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { NotAvailable } from '../../components/DrainDown/NotAvailable';
import AdditionalServices from '../../components/DrainDown/steps/AdditionalServices';
import DrainDownDate from '../../components/DrainDown/steps/DrainDownDate';
import OptionSelect from '../../components/DrainDown/steps/OptionSelect';
import ReconnectionDate from '../../components/DrainDown/steps/ReconnectionDate';
import Success from '../../components/DrainDown/steps/Success';
import {
  fromatBookedDates,
  getBaseTypeFromPackage,
  isDrainDownPackage,
  isParkValidForDrainDown,
} from '../../helpers/draindown';
import { fetchWrapper, swrFetcher, withApiBaseUrl } from '../../helpers/fetch';
import { drainDownConfirmation, pageView } from '../../helpers/googleTag';
import { Park } from '../../types/parks';

type DrainDownStep =
  | 'packages'
  | 'additional-services'
  | 'drain-down-dates'
  | 'reconnection-dates'
  | 'confirmation';

const defaultSteps: DrainDownStep[] = [
  'packages',
  'additional-services',
  'drain-down-dates',
  'reconnection-dates',
];

const DrainDownStepper = () => {
  const { loggedInUser, gtmPageViewAuthInfo, activeAccount, userType } =
    useAuthContext();

  const parkCode = activeAccount?.accountNo.substring(0, 2).toUpperCase();
  const { data, error } = useSWR<
    { drainDown: DrainDownSlot[]; reconnection: DrainDownSlot[] },
    Error
  >(`/draindown?parkCode=${parkCode}`, swrFetcher);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [steps, setSteps] = useState<DrainDownStep[]>(defaultSteps);
  const [form, setForm] = useState<DrainDownForm>({
    accountNumber: activeAccount?.accountNo || '',
    email: loggedInUser?.email || '',
    name: loggedInUser?.name || '',
    pitchName: activeAccount?.areaName || '',
    pitchNumber: activeAccount?.pitchNumber || '',
    keyBoxCode: '',
    option: null,
    drainDownSlotId: null,
    reconnectionSlotId: null,
    drainDownAdditionalExtras: [],
    moistureTrap: 0,
  });

  useEffect(() => {
    if (error) handleError(error.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      drainDownAdditionalExtras: [],
      moistureTrap: 0,
      accountNumber: activeAccount?.accountNo || '',
      pitchName: activeAccount?.areaName || '',
      pitchNumber: activeAccount?.pitchNumber || '',
    }));
  }, [activeAccount]);

  useEffect(() => {
    const step = steps[activeStep] || 'confirmation';
    pageView(
      'drain down',
      `/ga-virtual/owners-account/drain-down/${step}`,
      'owners account',
      gtmPageViewAuthInfo
    );
    if (activeStep === steps.length) {
      createDrainDown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, steps]);

  const normalizeValue = (value: string) => {
    if (['true', 'false'].includes(value)) {
      return value === 'true';
    }

    return value;
  };

  const handleFormChange: OnFormChange = (event) => {
    const {
      target: { name, value },
    } = event;

    if (name === 'option' && (value === 'diyLodge' || value === 'diyCaravan')) {
      setSteps((prev) => [...prev.slice(0, 2)]);
    }

    setForm({
      ...form,
      [name]: normalizeValue(value),
      ...(name === 'accountNumber' ? { pitchName: '' } : {}),
    });
  };

  const handleAdditionalExtraChange = (
    drainDownAdditionalExtras: string[],
    moistureTrap: number
  ) => {
    setForm((prev) => ({
      ...prev,
      drainDownAdditionalExtras,
      moistureTrap,
    }));
  };

  const handleBackStep = () => {
    if (activeStep - 1 === 0) {
      setSteps(defaultSteps);
      setForm((prev) => ({
        ...prev,
        drainDownAdditionalExtras: [],
        moistureTrap: 0,
      }));
    }
    setActiveStep(activeStep - 1);
  };

  const createDrainDown = async () => {
    const packageOptions =
      form.option === 'diyLodge' || form.option === 'diyCaravan'
        ? 'diy'
        : getBaseTypeFromPackage(form.option);
    setLoading(true);
    try {
      const response = await fetchWrapper(
        withApiBaseUrl('/draindown/booking'),
        {
          credentials: 'include',
          method: 'POST',
          body: JSON.stringify({
            accountNumber: form.accountNumber,
            keyBoxCode: form.keyBoxCode,
            choosenSlots: {
              draindown: form.drainDownSlotId,
              reconnection: form.reconnectionSlotId,
            },
            packageOptions,
            drainDownPackages: isDrainDownPackage(form.option)
              ? [form.option]
              : undefined,
            user: {
              name: form.name,
              email: form.email,
              pitchName: form.pitchName,
              pitchNumber: form.pitchNumber,
            },
            drainDownAdditionalExtras: form.drainDownAdditionalExtras,
            moistureTrap: form.moistureTrap,
            userType,
          }),
        }
      );
      setBookingId(response?.id);
    } catch (error) {
      handleError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleError = async (error = 'Something went wrong!') => {
    window.location.assign('/error');
    await drainDownConfirmation(
      {
        ...form,
        drainDownSlot: data?.drainDown?.find(
          (slot) => slot.id === form.drainDownSlotId
        ),
        reconnectionSlot: data?.reconnection?.find(
          (slot) => slot.id === form.reconnectionSlotId
        ),
      },
      {
        successFlag: 'error',
        errorType: error?.toLowerCase(),
      }
    );
  };

  const handleStepChange = async () => {
    setLoading(true);

    setActiveStep(activeStep + 1);
    window.scrollTo({ left: 0, top: 0 });
    setLoading(false);
  };

  const handleSkip = () => {
    setSteps((prev) => [...prev.filter((_, idx) => idx !== activeStep)]);
  };

  const getActiveStep = (currentStep: number) => {
    const step = steps[currentStep];
    switch (step) {
      case 'packages':
        return (
          <OptionSelect
            parkCode={parkCode}
            onStepChange={handleStepChange}
            onFormChange={handleFormChange}
          />
        );
      case 'additional-services':
        return (
          <AdditionalServices
            onStepChange={handleStepChange}
            handleAdditionalExtraChange={handleAdditionalExtraChange}
            option={form.option}
            drainDownAdditionalExtras={form.drainDownAdditionalExtras}
            moistureTrap={form.moistureTrap}
            parkCode={
              activeAccount?.accountNo.substring(0, 2).toUpperCase() || ''
            }
            handleSkip={handleSkip}
          />
        );
      case 'drain-down-dates':
        return (
          <DrainDownDate
            onStepChange={handleStepChange}
            onFormChange={handleFormChange}
            drainDownSlotId={form.drainDownSlotId}
            keyBoxCode={form.keyBoxCode}
            availableSlots={data?.drainDown}
          />
        );
      case 'reconnection-dates':
        return (
          <ReconnectionDate
            onStepChange={handleStepChange}
            onFormChange={handleFormChange}
            reconnectionSlotId={form.reconnectionSlotId}
            availableSlots={data?.reconnection}
          />
        );
      default:
        return null;
    }
  };

  if (bookingId) {
    return (
      <Success
        bookingId={bookingId}
        formData={form}
        drainDown={data?.drainDown}
        reconnection={data?.reconnection}
      />
    );
  }

  return (
    <div>
      {activeStep < steps.length && (
        <h1 className="owners-heading-1">Book a Drain Down</h1>
      )}

      {activeStep < steps.length && (
        <NumberedStepper steps={steps.length} activeStep={activeStep} />
      )}
      {activeStep !== 0 && activeStep < steps.length && (
        <Button onClick={handleBackStep} variant="text">
          Back
        </Button>
      )}
      {loading ? <LoadingIndicator loading /> : getActiveStep(activeStep)}
    </div>
  );
};

const DrainDownWrapper = () => {
  const { activeAccount } = useAuthContext();

  const isDrainDownAvailable = useMemo(() => {
    return activeAccount && isParkValidForDrainDown(activeAccount.parkCode);
  }, [activeAccount]);

  const park = useSWRImmutable<Park>(
    activeAccount?.parkCode && `/parks/${activeAccount.parkCode}`,
    swrFetcher
  ).data;

  const { data: currentBookings } = useSWR<DrainDownBooking>(
    activeAccount &&
      `/draindown/actual-bookings/${activeAccount?.accountNo.toUpperCase()}`,
    swrFetcher
  );

  if (!isDrainDownAvailable) return <NotAvailable />;
  if (currentBookings) {
    return currentBookings.drainDown && currentBookings.reconnection ? (
      <Banner
        fullWidthOnMobile
        typo="main"
        title="Book a Drain Down"
        content={
          <>
            <p className="owners-body">
              <b>Booked for {fromatBookedDates(currentBookings.drainDown)}</b>{' '}
              with reconnection{' '}
              {fromatBookedDates(currentBookings.reconnection)}.
            </p>
            <p className="owners-typography-2">
              To change your booking please call {park?.phoneNumber}.
            </p>
            <p className="owners-typography-2">
              The team will now process your Drain Down booking and it will
              appear on your owner account shortly.
            </p>
            <img
              style={{ filter: 'brightness(0)', marginTop: 20 }}
              src="/assets/hub/drainDown.svg"
              width={70}
              height={70}
              alt=""
            />
            <p className="owners-typography-2">
              Please check our terms and conditions{' '}
              <a
                href="https://ownershelp.haven.com/support/solutions/articles/103000093328-drain-down-terms-and-conditions"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              .
            </p>
          </>
        }
        footer={
          <InlineMessaging
            type={InlineMessagingTheme.INFO_THEME}
            title="Please note"
            message="Your Drain Down and reconnection will take please during the weeks stated, we cannot give an exact date but we'll always strive to complete all Drain Downs and Reconnections as early as possible in the week."
            dismissible={false}
          />
        }
      />
    ) : (
      <DrainDownStepper />
    );
  }
  return <LoadingIndicator loading />;
};

export const getStaticProps = () => ({
  props: {
    pageTitle: 'Drain down',
  },
  revalidate: 60 * 30,
});

export default DrainDownWrapper;
